import React, { useEffect, useMemo, useState } from 'react';
import RecordWizard from '../../../reusable/formRecords/RecordWizards';
import { Field, SectionHead, SectionDivider, ReadonlyBox, INP } from '../../../reusable/formRecords/formFieldPrimitives';
import ModuleScopeField from '../../shared/ModuleScopeField';
import { FieldRenderer, FormFieldSelectorModal } from './FormRegistryFields';
import { FieldSettingsDrawer } from './FormFieldConfigDrawers';
import { isStandardFormModified, resetStandardForm, useFormRegistryCollection } from './formRegistryStore';
import { useAuth } from '../../../../context/AuthContext';
import {
  appendFieldToSection,
  cloneFormBlueprint,
  createDraftForm,
  insertSectionAfter,
  getRegistrySubmoduleOptions,
  isBuiltInStandardField,
  matchesRegistryQuery,
  removeFieldFromSection,
  renameSectionInForm,
  resolveFormScopeLabel,
  updateFieldDefinition,
  updateFormMetadata,
} from './formRegistryData';
import { confirmAction } from '../../shared/SystemRowMenu';

const BADGE_STYLES = {
  standard: 'bg-red-50 text-red-500',
  custom: 'bg-blue-50 text-blue-600',
};

function titleCase(value) {
  return String(value ?? '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function badgeKind(form) {
  return String(form?.type ?? '').toLowerCase() === 'custom' ? 'custom' : 'standard';
}

function Badge({ children, kind = 'standard' }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold ${BADGE_STYLES[kind] ?? BADGE_STYLES.standard}`}>
      {children}
    </span>
  );
}

// Shown next to the type badge when a Standard form has been changed from
// its code default in Form Registry.
function ModifiedBadge({ form }) {
  if (!isStandardFormModified(form)) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold text-amber-600">
      Modified
    </span>
  );
}

function GridListToggle({ viewMode, onChange }) {
  return (
    <div className="flex items-center gap-4 text-xs font-semibold text-gray-500">
      <button
        type="button"
        onClick={() => onChange('grid')}
        className={`inline-flex items-center gap-1.5 ${viewMode === 'grid' ? 'text-green-600' : ''}`}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h6v6H4V5zm10 0h6v6h-6V5zM4 15h6v6H4v-6zm10 0h6v6h-6v-6z" />
        </svg>
        Grid View
      </button>
      <button
        type="button"
        onClick={() => onChange('list')}
        className={`inline-flex items-center gap-1.5 ${viewMode === 'list' ? 'text-green-600' : ''}`}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
        List View
      </button>
    </div>
  );
}

function RegistryCard({ form, onOpen, onDelete }) {
  const canDelete = String(form?.type ?? '').toLowerCase() === 'custom';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen?.();
        }
      }}
      className="flex min-h-[150px] flex-col justify-between rounded-2xl border border-gray-100 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="mb-5 flex items-start justify-end gap-3">
        {canDelete && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onDelete?.();
            }}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-red-100 bg-red-50 text-red-500 transition hover:border-red-200 hover:bg-red-100 hover:text-red-600"
            aria-label={`Delete ${form.name}`}
            title="Delete form"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5.5A1.5 1.5 0 0110.5 4h3A1.5 1.5 0 0115 5.5V7m-6 0h6m-7 0l1 12h6L16 7" />
            </svg>
          </button>
        )}
        <ModifiedBadge form={form} />
        <Badge kind={badgeKind(form)}>{titleCase(form.type)}</Badge>
      </div>
      <div>
        <h3 className="text-base font-bold text-gray-900">{form.name}</h3>
        <p className="mt-0.5 text-xs text-gray-500">{resolveFormScopeLabel(form) || 'Unassigned scope'}</p>
      </div>
    </div>
  );
}

function ListView({ forms, onOpen, onDelete }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-3 text-left">Form Name</th>
            <th className="px-4 py-3 text-left">Module Scope</th>
            <th className="px-4 py-3 text-right">Type</th>
            <th className="px-4 py-3 text-left">Sections</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {forms.map((form) => (
            <tr key={form.id} onClick={() => onOpen(form)} className="cursor-pointer hover:bg-gray-50">
              <td className="px-4 py-3 font-semibold text-gray-900">{form.name}</td>
              <td className="px-4 py-3 text-gray-600">{resolveFormScopeLabel(form) || 'Unassigned scope'}</td>
              <td className="px-4 py-3 text-right">
                <div className="inline-flex items-center gap-2">
                  <ModifiedBadge form={form} />
                  <Badge kind={badgeKind(form)}>{titleCase(form.type)}</Badge>
                </div>
              </td>
              <td className="px-4 py-3 text-gray-600">{form.sections?.length ?? 0}</td>
              <td className="px-4 py-3 text-right">
                {String(form?.type ?? '').toLowerCase() === 'custom' ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onDelete?.(form);
                    }}
                    className="inline-flex h-8 items-center rounded-full border border-red-100 bg-red-50 px-3 text-xs font-semibold text-red-600 transition hover:border-red-200 hover:bg-red-100"
                  >
                    Delete
                  </button>
                ) : (
                  <span className="text-xs font-medium text-gray-400">Standard</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScopePanel({ form, designerMode, onMetadataChange }) {
  const editable = designerMode && String(form.type ?? '').toLowerCase() === 'custom';
  const submoduleOptions = getRegistrySubmoduleOptions(form.moduleKey);

  if (editable) {
    return (
      <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <SectionHead title="Form Scope" />
        <div className="mt-4">
          <ModuleScopeField
            singleSelect
            singleSelectLayout="dropdown"
            label="Module Scope"
            required
            value={form.moduleKey}
            onModuleChange={(moduleKey) => onMetadataChange({ moduleKey, submoduleKey: '' })}
            moduleValue={form.moduleKey}
            submoduleValue={form.submoduleKey}
            onSubmoduleChange={(submoduleKey) => onMetadataChange({ submoduleKey })}
            submoduleOptions={submoduleOptions}
            submoduleLabel="Sub Module"
            note="Bind this custom form to one module and one submodule."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-bold uppercase tracking-wide text-gray-900">{form.name}</h2>
          <p className="mt-1 text-sm text-gray-400">{resolveFormScopeLabel(form) || 'Unassigned scope'}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge kind={badgeKind(form)}>{titleCase(form.type)}</Badge>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Module">
          <ReadonlyBox>{form.moduleName || form.moduleKey || 'Unassigned'}</ReadonlyBox>
        </Field>
        <Field label="Sub Module">
          <ReadonlyBox>{form.submoduleName || form.submoduleKey || 'Unassigned'}</ReadonlyBox>
        </Field>
      </div>
    </div>
  );
}

function SectionBlock({
  formId,
  form,
  section,
  designerMode,
  isLast,
  onAddField,
  onAddSection,
  onRemoveField,
  onFieldChange,
  onFieldOtherChange,
  onEditField,
  onRenameSection,
}) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState(section.title);
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');

  useEffect(() => {
    if (!editingTitle) setDraftTitle(section.title);
  }, [editingTitle, section.title]);

  useEffect(() => {
    if (!designerMode) {
      setEditingTitle(false);
      setAddSectionOpen(false);
      setNewSectionTitle('');
    }
  }, [designerMode]);

  const commitTitle = () => {
    const nextTitle = draftTitle.trim();
    if (nextTitle && nextTitle !== section.title) {
      onRenameSection(formId, section.id, nextTitle);
    }
    setEditingTitle(false);
  };

  const submitNewSection = () => {
    const title = newSectionTitle.trim();
    if (!title) return;
    onAddSection(section.id, title);
    setNewSectionTitle('');
    setAddSectionOpen(false);
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {editingTitle ? (
              <input
                autoFocus
                value={draftTitle}
                onChange={(event) => setDraftTitle(event.target.value)}
                onBlur={commitTitle}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') commitTitle();
                  if (event.key === 'Escape') {
                    setDraftTitle(section.title);
                    setEditingTitle(false);
                  }
                }}
                className="rounded-lg border border-green-300 bg-white px-2.5 py-1.5 text-xs font-bold uppercase tracking-wider text-gray-900 outline-none"
              />
            ) : (
              <SectionHead title={section.title} />
            )}

            {designerMode && !editingTitle && !section.locked && (
              <button
                type="button"
                onClick={() => setEditingTitle(true)}
                className="text-gray-400 hover:text-gray-600"
                title="Rename section"
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                </svg>
              </button>
            )}
          </div>

          {designerMode && !section.locked && (
            <button
              type="button"
              onClick={() => setAddSectionOpen((open) => !open)}
              className={`inline-flex h-5 w-5 items-center justify-center transition hover:text-green-600 ${addSectionOpen ? 'text-green-600' : 'text-gray-400'}`}
              title="Add a new section after this one"
              aria-label="Add a new section after this one"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {section.fields.map((field) => (
            <FieldRenderer
              key={field.id}
              field={field}
              value={field.value}
              otherValue={field.otherValue}
              disabled={!designerMode || Boolean(section.locked)}
              onRemove={designerMode && !section.locked && !isBuiltInStandardField(form, field.id) ? () => onRemoveField(section.id, field.id) : undefined}
              onEdit={designerMode && !section.locked ? () => onEditField(section.id, field) : undefined}
              onFieldChange={(fieldId, value, label) => onFieldChange(section.id, fieldId, value, label)}
              onOtherChange={(otherFieldKey, value) => onFieldOtherChange(section.id, field.id, value)}
            />
          ))}
        </div>

        {designerMode && !section.locked && (
          <button
            type="button"
            onClick={() => onAddField(section.id)}
            className="w-full rounded-xl border-2 border-dashed border-green-300 bg-green-50 py-3 text-sm font-bold text-green-600 transition-colors hover:bg-green-100"
          >
            + Add
          </button>
        )}

        {addSectionOpen && (
          <div className="space-y-3 rounded-2xl border border-dashed border-green-200 bg-green-50/40 p-4">
            <Field label="Section Title" required hint="This section is inserted right after this one.">
              <input
                autoFocus
                value={newSectionTitle}
                onChange={(event) => setNewSectionTitle(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') submitNewSection();
                  if (event.key === 'Escape') {
                    setNewSectionTitle('');
                    setAddSectionOpen(false);
                  }
                }}
                placeholder="e.g. Animal Data"
                className={INP}
              />
            </Field>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setNewSectionTitle('');
                  setAddSectionOpen(false);
                }}
                className="h-10 flex-1 rounded-full border border-gray-200 bg-white text-xs font-semibold text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!newSectionTitle.trim()}
                onClick={submitNewSection}
                className="h-10 flex-1 rounded-full bg-green-600 text-xs font-bold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                + Add Section
              </button>
            </div>
          </div>
        )}
      </div>

      {!isLast && <SectionDivider />}
    </>
  );
}

function FormViewer({ form, canEditDesign, onBackToRegistry, onOpenDesigner, onResetToDefault, onFieldChange }) {
  const modified = isStandardFormModified(form);

  return (
    <div className="mx-auto max-w-4xl rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-bold uppercase tracking-wide text-gray-900">{form.name}</h2>
          <p className="mt-1 text-sm text-gray-400">{resolveFormScopeLabel(form) || 'Unassigned scope'}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {canEditDesign && modified && (
            <button type="button" onClick={onResetToDefault} className="text-xs font-bold text-amber-600 hover:text-amber-700">
              Reset to Default
            </button>
          )}
          {canEditDesign && (
            <button type="button" onClick={onOpenDesigner} className="text-xs font-bold text-green-600 hover:text-green-700">
              Edit Design
            </button>
          )}
          <ModifiedBadge form={form} />
          <Badge kind={badgeKind(form)}>{titleCase(form.type)}</Badge>
        </div>
      </div>

      <div className="space-y-4">
        <ScopePanel form={form} designerMode={false} onMetadataChange={() => {}} />

        {form.sections.map((section, index) => (
          <SectionBlock
            key={section.id}
            formId={form.id}
            form={form}
            section={section}
            designerMode={false}
            isLast={index === form.sections.length - 1}
            onAddField={() => {}}
            onAddSection={() => {}}
            onRemoveField={() => {}}
            onFieldChange={(sectionId, fieldId, value, label) => onFieldChange(form.id, sectionId, fieldId, value, label)}
            onFieldOtherChange={() => {}}
            onEditField={() => {}}
            onRenameSection={() => {}}
          />
        ))}
      </div>

      <div className="mt-6 flex gap-3 border-t border-gray-100 pt-6">
        <button
          type="button"
          onClick={onBackToRegistry}
          className="h-11 flex-1 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50"
        >
          Go Back
        </button>
      </div>
    </div>
  );
}

function FormTitlePanel({ form, onRenameForm }) {
  const [draftTitle, setDraftTitle] = useState(form.name);

  useEffect(() => {
    setDraftTitle(form.name);
  }, [form.id, form.name]);

  const commit = () => {
    const next = draftTitle.trim();
    if (next !== form.name) onRenameForm(form.id, next);
  };

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <SectionHead title="Form Title" />
      <div className="mt-4">
        <Field label="Form Title" required>
          <input
            value={draftTitle}
            onChange={(event) => setDraftTitle(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                commit();
                event.currentTarget.blur();
              }
            }}
            placeholder="e.g. Seasonal Migration Notice"
            className={INP}
          />
        </Field>
      </div>
    </div>
  );
}

function FormDesigner({
  form,
  onBackToRegistry,
  onSaveDesign,
  onAddField,
  onAddSection,
  onRemoveField,
  onFieldChange,
  onFieldOtherChange,
  onRenameForm,
  onRenameSection,
  onMetadataChange,
  onEditField,
  saveError,
}) {
  const isCustom = String(form.type || '').toLowerCase() === 'custom';
  const hasScope = Boolean(form.moduleKey && form.submoduleKey);
  const canSave = Boolean(form.name.trim()) && (!isCustom || hasScope);

  return (
    <>
      <RecordWizard
        steps={[{ key: 'design', label: 'Design Form' }]}
        currentStep={0}
        title={isCustom ? (form.name.trim() || 'Untitled Form') : form.name}
        subtitle="Configure the registry schema, metadata, and fields for this form"
        statusLabel={form.status}
        showStatus={false}
        onCancel={onBackToRegistry}
        error={saveError}
        footer={(
          <>
            <button
              type="button"
              onClick={onBackToRegistry}
              className="h-11 flex-1 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSaveDesign}
              disabled={!canSave}
              className="h-11 flex-1 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Save Form
            </button>
          </>
        )}
      >
        {!isCustom && (
          <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs text-amber-800">
            Saving applies your changes to this standard form wherever it is used: fields you add appear on the
            module&apos;s Add Record page under &quot;Additional Fields&quot;. Use &quot;Reset to Default&quot; to undo all changes.
          </div>
        )}

        {isCustom && (
          <FormTitlePanel
            form={form}
            onRenameForm={onRenameForm}
          />
        )}

        <ScopePanel
          form={form}
          designerMode
          onMetadataChange={(changes) => onMetadataChange(form.id, changes)}
        />

        {hasScope && form.sections.map((section, index) => (
          <SectionBlock
            key={section.id}
            formId={form.id}
            form={form}
            section={section}
            designerMode
            isLast={index === form.sections.length - 1}
            onAddField={onAddField}
            onAddSection={(afterSectionId, title) => onAddSection(form.id, afterSectionId, title)}
            onRemoveField={(sectionId, fieldId) => onRemoveField(form.id, sectionId, fieldId)}
            onFieldChange={(sectionId, fieldId, value, label) => onFieldChange(form.id, sectionId, fieldId, value, label)}
            onFieldOtherChange={(sectionId, fieldId, otherValue) => onFieldOtherChange(form.id, sectionId, fieldId, otherValue)}
            onEditField={(sectionId, field) => onEditField(form.id, sectionId, field)}
            onRenameSection={(formId, sectionId, title) => onRenameSection(formId, sectionId, title)}
          />
        ))}
      </RecordWizard>
    </>
  );
}

export default function FormRegistry() {
  const { user } = useAuth();
  const currentUserId = user?.uuid || user?.email || '';
  // Class B creation rights: R01 and designated National Data Administrators
  // only — no State or LGA user may create a form.
  const canCreateForm = user?.roleId === 'R01';
  // Changing a Standard form changes the live module pages, so it carries
  // the same restriction as creating a form.
  const canEditDesign = (form) => String(form?.type ?? '').toLowerCase() === 'custom' || user?.roleId === 'R01';
  const [forms, setForms] = useFormRegistryCollection();
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [activeFormId, setActiveFormId] = useState('');
  const [mode, setMode] = useState('registry');
  const [pendingFieldTarget, setPendingFieldTarget] = useState(null);
  const [fieldEditor, setFieldEditor] = useState(null);
  const [saveError, setSaveError] = useState('');
  // A brand-new form-in-progress lives here — not in `forms` / localStorage —
  // until "Save Form" is clicked, so an abandoned Create New never leaves a
  // stray draft card behind in the registry.
  const [draftForm, setDraftForm] = useState(null);

  useEffect(() => {
    if (mode === 'registry') {
      setPendingFieldTarget(null);
      setFieldEditor(null);
    }
  }, [mode]);

  const filteredForms = useMemo(() => forms.filter((form) => matchesRegistryQuery(form, search)), [forms, search]);
  const activeForm = (draftForm && draftForm.id === activeFormId)
    ? draftForm
    : (forms.find((form) => form.id === activeFormId) || null);

  const updateActiveForm = (updater) => {
    if (!activeFormId) return;
    setForms((prev) => prev.map((form) => (form.id === activeFormId ? updater(form) : form)));
  };

  // Routes an edit to whichever store currently owns the form — the
  // in-progress draftForm, or the persisted forms collection.
  const applyFormUpdate = (formId, updater) => {
    if (draftForm && draftForm.id === formId) {
      setDraftForm((prev) => (prev && prev.id === formId ? updater(prev) : prev));
      return;
    }
    setForms((prev) => prev.map((form) => (form.id === formId ? updater(form) : form)));
  };

  const openForm = (form) => {
    setDraftForm(null);
    setActiveFormId(form.id);
    setMode('view');
    setPendingFieldTarget(null);
    setFieldEditor(null);
    setSaveError('');
  };

  const goBackToRegistry = () => {
    setDraftForm(null);
    setActiveFormId('');
    setMode('registry');
    setPendingFieldTarget(null);
    setFieldEditor(null);
    setSaveError('');
  };

  // Create New opens the designer on a fresh, unsaved draft (Form Scope +
  // an empty "General Information" section) — the Field Selector only
  // opens once the user clicks that section's own "+ Add" button.
  const createNewForm = () => {
    if (!canCreateForm) return;
    const draft = createDraftForm({ createdBy: currentUserId });
    setDraftForm(draft);
    setActiveFormId(draft.id);
    setMode('designer');
    setPendingFieldTarget(null);
    setFieldEditor(null);
    setSaveError('');
  };

  // Cancelling the field selector just closes the picker and returns to
  // wherever "+ Add" was clicked from (the designer screen) — it never
  // navigates away or discards the draft/form being built.
  const handleFieldSelectorCancel = () => {
    setPendingFieldTarget(null);
    setFieldEditor(null);
    setSaveError('');
  };

  const handleRequestFieldPicker = (formId, sectionId) => {
    setPendingFieldTarget({ formId, sectionId });
    setFieldEditor(null);
    setSaveError('');
  };

  const handleAddSection = (formId, afterSectionId, title) => {
    applyFormUpdate(formId, (form) => insertSectionAfter(form, afterSectionId, title));
  };

  const handleRemoveField = (formId, sectionId, fieldId) => {
    applyFormUpdate(formId, (form) => removeFieldFromSection(form, sectionId, fieldId));
  };

  const handleRenameForm = (formId, name) => {
    applyFormUpdate(formId, (form) => updateFormMetadata(form, { name }));
  };

  const handleRenameSection = (formId, sectionId, title) => {
    applyFormUpdate(formId, (form) => renameSectionInForm(form, sectionId, title));
  };

  const handleMetadataChange = (formId, changes) => {
    applyFormUpdate(formId, (form) => updateFormMetadata(form, changes));
  };

  const handleFieldPreviewChange = (formId, sectionId, fieldId, value) => {
    applyFormUpdate(formId, (form) => updateFieldDefinition(form, sectionId, fieldId, { value }));
  };

  const handleFieldPreviewOtherChange = (formId, sectionId, fieldId, otherValue) => {
    applyFormUpdate(formId, (form) => updateFieldDefinition(form, sectionId, fieldId, { otherValue }));
  };

  const handleBackToFieldSelector = () => {
    if (!fieldEditor) return;
    setPendingFieldTarget({ formId: fieldEditor.formId, sectionId: fieldEditor.sectionId });
    setFieldEditor(null);
    setSaveError('');
  };

  const handleDeleteForm = async (form) => {
    if (String(form?.type ?? '').toLowerCase() !== 'custom') return;
    const confirmed = await confirmAction({ title: 'Delete Form?', message: `Delete "${form.name}"? This cannot be undone.`, confirmLabel: 'Delete', danger: true });
    if (!confirmed) return;

    setForms((prev) => prev.filter((item) => item.id !== form.id));
    if (activeFormId === form.id) {
      goBackToRegistry();
    }
  };

  const handleEditField = (formId, sectionId, field) => {
    setFieldEditor({
      formId,
      sectionId,
      field: {
        ...field,
        options: Array.isArray(field.options) ? field.options.map((option) => (typeof option === 'object' ? { ...option } : option)) : [],
        validation: field.validation ? { ...field.validation } : undefined,
      },
      isNew: false,
    });
    setPendingFieldTarget(null);
  };

  const handleSaveField = (field) => {
    if (!fieldEditor || !activeForm) return;

    applyFormUpdate(fieldEditor.formId, (form) => (
      fieldEditor.isNew
        ? appendFieldToSection(form, fieldEditor.sectionId, field)
        : updateFieldDefinition(form, fieldEditor.sectionId, fieldEditor.field.id, field)
    ));

    setFieldEditor(null);
    setPendingFieldTarget(null);
  };

  const handleSaveDesign = () => {
    if (!activeForm) return;

    if (String(activeForm.type ?? '').toLowerCase() === 'custom' && (!activeForm.moduleKey || !activeForm.submoduleKey)) {
      setSaveError('Select a module and sub module before saving this custom form.');
      return;
    }

    setSaveError('');

    // The designer always edits a working copy (draftForm). Saving commits
    // it: an existing form is replaced in place, a brand-new one is added.
    // Only now does it reach `forms` / localStorage — and, for a Standard
    // form, the module's Add Record page.
    if (draftForm && draftForm.id === activeForm.id) {
      const finalized = updateFormMetadata(draftForm, {});
      setForms((prev) => (
        prev.some((form) => form.id === finalized.id)
          ? prev.map((form) => (form.id === finalized.id ? finalized : form))
          : [finalized, ...prev]
      ));
      setDraftForm(null);
      setMode('view');
      return;
    }

    updateActiveForm((form) => updateFormMetadata(form, {}));
    setMode('view');
  };

  const handleResetToDefault = async (form) => {
    const confirmed = await confirmAction({ title: 'Reset to Default?', message: `Reset "${form.name}" to its default design? All changes made to this standard form will be removed.`, confirmLabel: 'Reset' });
    if (!confirmed) return;
    // The store listens for this write and reloads the default design.
    resetStandardForm(form.id);
  };

  const handleEditorCancel = () => {
    setFieldEditor(null);
    setPendingFieldTarget(null);
  };

  if (activeForm) {
    return (
      <div className="min-h-screen bg-white p-6">
        {mode === 'designer' ? (
          <FormDesigner
            form={activeForm}
            onBackToRegistry={goBackToRegistry}
            onSaveDesign={handleSaveDesign}
            onAddField={(sectionId) => handleRequestFieldPicker(activeForm.id, sectionId)}
            onAddSection={handleAddSection}
            onRemoveField={handleRemoveField}
            onFieldChange={handleFieldPreviewChange}
            onFieldOtherChange={handleFieldPreviewOtherChange}
            onRenameForm={handleRenameForm}
            onRenameSection={handleRenameSection}
            onMetadataChange={handleMetadataChange}
            onEditField={handleEditField}
            saveError={saveError}
          />
        ) : (
          <FormViewer
            form={activeForm}
            canEditDesign={canEditDesign(activeForm)}
            onBackToRegistry={goBackToRegistry}
            onOpenDesigner={() => {
              // Edit a working copy so Cancel discards every change.
              setDraftForm(cloneFormBlueprint(activeForm));
              setMode('designer');
            }}
            onResetToDefault={() => handleResetToDefault(activeForm)}
            onFieldChange={handleFieldPreviewChange}
          />
        )}

        {pendingFieldTarget && !fieldEditor && (
          <FormFieldSelectorModal
            open
            onClose={handleFieldSelectorCancel}
            onSelectField={(field) => {
              setFieldEditor({
                formId: pendingFieldTarget.formId,
                sectionId: pendingFieldTarget.sectionId,
                field,
                isNew: true,
              });
              setPendingFieldTarget(null);
            }}
          />
        )}

        {fieldEditor && fieldEditor.field && (
          <FieldSettingsDrawer
            open
            field={fieldEditor.field}
            onClose={handleEditorCancel}
            onBack={fieldEditor.isNew ? handleBackToFieldSelector : undefined}
            onSave={handleSaveField}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="mb-4 flex items-center gap-3">
        <label className="flex flex-1 items-center gap-2.5 rounded-2xl border border-gray-200 bg-white px-3.5 py-2.5 focus-within:border-green-300 focus-within:ring-2 focus-within:ring-green-100">
          <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') setSearch(searchDraft);
            }}
            placeholder="Search here"
            className="w-full border-0 bg-transparent text-sm text-gray-700 outline-none placeholder-gray-400"
          />
        </label>
        <button
          type="button"
          onClick={() => setSearch(searchDraft)}
          className="rounded-lg border border-green-200 bg-green-50 px-5 py-2.5 text-sm font-semibold text-green-700 hover:bg-green-100"
        >
          Search
        </button>
        <button
          type="button"
          onClick={createNewForm}
          disabled={!canCreateForm}
          title={canCreateForm ? undefined : 'Only R01 (Administrator) and designated National Data Administrators may create a form.'}
          className="flex items-center gap-1.5 rounded-lg bg-green-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <span className="text-base font-bold leading-none">+</span> Create New
        </button>
      </div>

      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-bold text-gray-900">Form Registry</h2>
        <GridListToggle viewMode={viewMode} onChange={setViewMode} />
      </div>

      {filteredForms.length === 0 ? (
        <div className="py-16 text-center text-sm text-gray-400">No forms match your search.</div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {filteredForms.map((form) => (
            <RegistryCard key={form.id} form={form} onOpen={() => openForm(form)} onDelete={() => handleDeleteForm(form)} />
          ))}
        </div>
      ) : (
        <ListView forms={filteredForms} onOpen={openForm} onDelete={handleDeleteForm} />
      )}
    </div>
  );
}
